import Seo from "@/components/feature/Seo";
import PageHero from "@/components/feature/PageHero";
import Reveal from "@/components/base/Reveal";
import SectionHeading from "@/components/base/SectionHeading";
import ContactEnquiryForm from "@/components/feature/ContactEnquiryForm";

const enquiryTypes = [
  { icon: "ri-question-answer-line", title: "General Enquiry", text: "Questions about the collaboration, our approach or how to get started." },
  { icon: "ri-flashlight-line", title: "Retrofit Enquiry", text: "Discuss a specific vehicle and whether a retrofit can be considered." },
  { icon: "ri-roadster-line", title: "Fleet Enquiry", text: "Fleet electrification planning for multiple vehicles and operations." },
  { icon: "ri-store-3-line", title: "Dealer / Workshop Enquiry", text: "Explore working with us as a dealer or workshop partner." },
  { icon: "ri-links-line", title: "Technology Partnership", text: "Discuss technology, integration or ecosystem collaboration." },
];

export default function Contact() {
  return (
    <>
      <Seo
        title="Contact Us | KAL LORDS ZERO21 DJT EV Retrofit"
        description="Contact the KAL LORDS ZERO21 DJT EV retrofit team for general, retrofit, fleet, dealer and technology partnership enquiries."
        keywords="EV retrofit contact, EV conversion enquiry, fleet electrification enquiry"
        path="/contact"
      />
      <PageHero
        eyebrow="Contact"
        breadcrumb="Contact"
        title="Talk to Our Team"
        description="Whether you own a single vehicle or operate a fleet, the right starting point is a conversation about what is technically possible."
      />

      <section className="w-full bg-background-50 py-16 md:py-24">
        <div className="mx-auto w-full max-w-container px-4 md:px-6">
          <Reveal>
            <SectionHeading
              eyebrow="Enquiry Types"
              title="Choose the Right Enquiry"
              description="Selecting the correct enquiry type routes your message to the right team and helps us respond usefully."
            />
          </Reveal>
          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {enquiryTypes.map((item, index) => (
              <Reveal key={item.title} delay={index * 0.05}>
                <div className="flex h-full flex-col rounded-lg border border-background-200 bg-background-100 p-5">
                  <span className="flex h-11 w-11 items-center justify-center rounded-md bg-background-50 text-primary-700">
                    <i className={`${item.icon} text-xl`} />
                  </span>
                  <h3 className="mt-4 text-sm font-semibold text-foreground-950">{item.title}</h3>
                  <p className="mt-2 text-xs leading-relaxed text-foreground-600">{item.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full bg-background-100 py-16 md:py-24">
        <div className="mx-auto w-full max-w-container px-4 md:px-6">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
            <Reveal className="lg:col-span-4">
              <SectionHeading
                eyebrow="Send a Message"
                title="We'll Route Your Enquiry"
                description="Complete the form and the team will respond through the official channel."
              />

              <div className="mt-8 space-y-4">
                <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <div className="flex items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-800">
                      <i className="ri-map-pin-line text-lg" />
                    </span>
                    <div>
                      <p className="font-label text-xs font-semibold uppercase tracking-[0.14em] text-foreground-500">
                        Office Location
                      </p>
                      <p className="mt-1 text-sm text-foreground-700">
                        Official address details to be announced.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <div className="flex items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-800">
                      <i className="ri-phone-line text-lg" />
                    </span>
                    <div>
                      <p className="font-label text-xs font-semibold uppercase tracking-[0.14em] text-foreground-500">
                        Phone & Email
                      </p>
                      <p className="mt-1 text-sm text-foreground-700">
                        Official contact details will be published once verified. Please use the enquiry
                        form so your message is not missed.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <p className="mt-6 text-xs leading-relaxed text-foreground-500">
                <i className="ri-shield-check-line mr-2" />
                We only publish verified contact information and never share your details with third
                parties.
              </p>
            </Reveal>

            <Reveal delay={0.12} className="lg:col-span-8">
              <ContactEnquiryForm />
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}